// Thread + project store. Threads are project-scoped (refetched on the
// `threads_changed` SSE event); projects are global (refetched on
// `project_changed`). Thread mutations apply optimistically so the UI
// reacts before the round-trip, then reconcile with the server row.

import { useCallback, useRef, useState } from 'react'
import { api, createThreadWithPreference } from '../api'
import type { Project, Thread } from './types'

export interface ThreadStore {
  threads: Thread[]
  projects: Project[]
  /** Raw replace — used by the boot / project-switch path. */
  setThreads: (threads: Thread[]) => void
  setProjects: (projects: Project[]) => void
  /** Refetch one project's threads. Guarded against a stale cross-project
   *  fetch landing after the user switched away. */
  refetchThreads: (projectId: string) => Promise<void>
  refetchProjects: () => Promise<void>
  /** Create a thread (inherits the locally-remembered tier) and prepend it. */
  createThread: (projectId: string, title?: string | null) => Promise<Thread>
  /** Rename — optimistic, then reconciled with the server row. */
  renameThread: (threadId: string, title: string) => Promise<void>
  /** Archive — server soft-delete, then drop from the local list. */
  archiveThread: (threadId: string) => Promise<void>
  /** Optimistically advance the read cursor; fire-and-forget the persist. */
  markRead: (threadId: string) => void
  /** Generic local patch — turn-completion bumps (last_message_at, etc.). */
  updateThread: (threadId: string, patch: (t: Thread) => Thread) => void
  updateProject: (projectId: string, patch: (p: Project) => Project) => void
}

export function useThreadStore(activeProjectId: string | null): ThreadStore {
  const [threads, setThreadsState] = useState<Thread[]>([])
  const [projects, setProjectsState] = useState<Project[]>([])
  const activeRef = useRef(activeProjectId)
  activeRef.current = activeProjectId

  const setThreads = useCallback((t: Thread[]) => setThreadsState(t), [])
  const setProjects = useCallback((p: Project[]) => setProjectsState(p), [])

  const refetchThreads = useCallback(async (projectId: string) => {
    const fresh = await api.listThreads(projectId)
    if (activeRef.current === projectId) setThreadsState(fresh)
  }, [])

  const refetchProjects = useCallback(async () => {
    setProjectsState(await api.listProjects())
  }, [])

  const updateThread = useCallback(
    (threadId: string, patch: (t: Thread) => Thread) => {
      setThreadsState((prev) =>
        prev.map((t) => (t.id === threadId ? patch(t) : t)),
      )
    },
    [],
  )

  const updateProject = useCallback(
    (projectId: string, patch: (p: Project) => Project) => {
      setProjectsState((prev) =>
        prev.map((p) => (p.id === projectId ? patch(p) : p)),
      )
    },
    [],
  )

  const createThread = useCallback(
    async (
      projectId: string,
      title: string | null = null,
    ): Promise<Thread> => {
      const t = await createThreadWithPreference(projectId, title)
      setThreadsState((prev) => [t, ...prev])
      return t
    },
    [],
  )

  const renameThread = useCallback(
    async (threadId: string, title: string) => {
      updateThread(threadId, (t) => ({ ...t, title }))
      try {
        const updated = await api.renameThread(threadId, title)
        setThreadsState((prev) =>
          prev.map((t) => (t.id === threadId ? updated : t)),
        )
      } catch {
        /* cosmetic — the optimistic title stands; a refetch corrects it */
      }
    },
    [updateThread],
  )

  const archiveThread = useCallback(async (threadId: string) => {
    await api.archiveThread(threadId)
    setThreadsState((prev) => prev.filter((t) => t.id !== threadId))
  }, [])

  const markRead = useCallback(
    (threadId: string) => {
      const now = new Date().toISOString()
      updateThread(threadId, (t) => ({ ...t, last_read_at: now }))
      api.markThreadRead(threadId).catch(() => {})
    },
    [updateThread],
  )

  return {
    threads,
    projects,
    setThreads,
    setProjects,
    refetchThreads,
    refetchProjects,
    createThread,
    renameThread,
    archiveThread,
    markRead,
    updateThread,
    updateProject,
  }
}
